
import { Component, Prop, Vue } from "vue-property-decorator";
import PModal from "@/components/look/PModal.vue";
import { VueComponent } from "@/vue-ts-component";

import { Action, Getter } from "vuex-class";
import {
  ISolCustomerGetAssistanceServiceResponse,
  ISolCustomerGetDeliveryServiceResponse,
  ISolCustomerGetPickUpServiceResponse,
  ISolCustomerGetServiceParams,
  ISolCustomerGetSupplyMaterialServiceResponse,
} from "@/services";
import { BModal } from "bootstrap-vue";
import {
  IAssistanceService,
  ICountryConfig,
  IDeliveryService,
  IOrderConfigCommon,
  IPickUpService,
  IRequestHistorialsService,
  ISolCustomerCancellationReason,
  ISolCustomerOrderService,
  ISolCustomerPack,
  ISolCustomerSupplyMaterialService,
  IUserDetailData,
} from "@/entities";
import VueFilters from "@/vue-filters";
import { mapGetters } from "vuex";
import DetailPickup from "@/components/orders/DetailPickup.vue";
import DetailDelivery from "@/components/orders/DetailDelivery.vue";
import DetailAssistance from "@/components/orders/DetailAssistance.vue";
import DetailSupply from "@/components/orders/DetailSupply.vue";
import DetailConsignment from "@/components/orders/DetailConsignment.vue";

@Component({
  name: "detail-order-modal",
  mixins: [VueFilters],
  computed: {
    ...mapGetters(["getLocale"]),
  },
  components: {
    DetailConsignment,
    PModal,
    DetailPickup,
    DetailDelivery,
    DetailAssistance,
    DetailSupply,
  },
})
export default class DetailOrderModal extends Vue {
  @Getter("getLoggedUser") getLoggedUser!: IUserDetailData;
  @Getter("getCountryConfig") countryConfig!: ICountryConfig | undefined;
  @Action("fetchPickUpService") fetchPickUpService!: (
    params: ISolCustomerGetServiceParams
  ) => Promise<ISolCustomerGetPickUpServiceResponse | undefined>;
  @Action("fetchAssistanceService") fetchAssistanceService!: (
    params: ISolCustomerGetServiceParams
  ) => Promise<ISolCustomerGetAssistanceServiceResponse | undefined>;
  @Action("fetchDeliveryService") fetchDeliveryService!: (
    params: ISolCustomerGetServiceParams
  ) => Promise<ISolCustomerGetDeliveryServiceResponse | undefined>;
  @Action("fetchSupplyMaterialService") fetchSupplyMaterialService!: (
    params: ISolCustomerGetServiceParams
  ) => Promise<ISolCustomerGetSupplyMaterialServiceResponse | undefined>;
  @Prop({ required: true })
  entityId!: string | undefined;
  @Prop({ required: true })
  cancellationReasons!: ISolCustomerCancellationReason[];
  @Prop({ required: false, default: false })
  agilePickup!: boolean;
  @Prop({ required: true })
  packs!: ISolCustomerPack[];
  @Prop({ required: false, default: false }) isOrdersTable!: boolean;

  serviceStatusCode = "";
  serviceId: string | undefined;
  serviceCode: string | undefined = "";
  serviceType: number | undefined | null = null;
  serviceTypeDesc: string | undefined = "";
  creationDate: string | null = null;
  service: ISolCustomerOrderService | undefined = {};
  pickUpService: IPickUpService | undefined = {};
  assistanceService: IAssistanceService | undefined = {};
  deliveryService: IDeliveryService | undefined = {};
  supplyMaterialService: ISolCustomerSupplyMaterialService | undefined = {};

  async mounted(): Promise<void> {
    this.$on("showDetailOrderModal", (data: IRequestHistorialsService) => {
      this.showModal(data);
    });
  }

  async beforeDestroy(): Promise<void> {
    this.$off("showDetailOrderModal");
  }

  async showModal(data: IRequestHistorialsService): Promise<void> {
    const countryCode = this.getLoggedUser.country?.code ?? "";
    this.serviceType = data.serviceType;
    this.serviceId = data.serviceId;
    this.serviceCode = data.serviceCode;
    this.creationDate = data.requestedTime || null;
    this.serviceStatusCode = data.serviceStatusCode || "";
    this.service = data;
    const params = {
      countryCode: countryCode,
      serviceId: this.serviceId,
    } as ISolCustomerGetServiceParams;

    if (data.productCode && data.productDescription) {
      this.serviceTypeDesc = this.$t(data.productDescription).toString();
      switch (data.productCode) {
        case "1":
          this.pickUpService = data;
          if (!this.isOrdersTable) await this.getPickUpService(params);
          break;
        case "11":
          this.deliveryService = data;
          if (!this.isOrdersTable) await this.getDeliveryService(params);
          break;
        case "40":
          this.assistanceService = data;
          if (!this.isOrdersTable) await this.getAssistanceService(params);
          break;
        case "14":
          this.supplyMaterialService = data;
          if (!this.isOrdersTable) await this.getSupplyMaterialServiceService(params);
          break;
      }
    } else {
      switch (this.serviceType) {
        case 0:
          this.pickUpService = data;
          this.serviceTypeDesc = this.$t("label.pickup").toString();
          if (!this.isOrdersTable) await this.getPickUpService(params);
          break;
        case 1:
          this.deliveryService = data;
          if (data.isChange) {
            this.serviceTypeDesc = this.$t("label.deliveryExchange").toString();
          } else {
            this.serviceTypeDesc = this.$t("label.delivery").toString();
          }
          if (!this.isOrdersTable) await this.getDeliveryService(params);
          break;
        case 3:
          this.assistanceService = data;
          this.serviceTypeDesc = this.$t("label.technicalAssistance").toString();
          if (!this.isOrdersTable) await this.getAssistanceService(params);
          break;
        case 4:
          this.supplyMaterialService = data;
          this.serviceTypeDesc = this.$t("label.supplyMaterialService").toString();
          if (!this.isOrdersTable) await this.getSupplyMaterialServiceService(params);
          break;
      }
    }

    this.$set(
      this.service!,
      "motiveDescription",
      this.cancellationReasons?.find((element) => element.code === this.service?.motiveCode)
        ?.description || this.service?.motiveCode
    );

    const pModal = this.$refs.orderDetailModal as VueComponent<any>;
    const bModal = pModal.$children[0] as BModal;
    bModal.show();
  }

  async onRefreshDeclaredValues(): Promise<void> {
    const countryCode = this.getLoggedUser.country?.code ?? "";
    const params = {
      countryCode: countryCode,
      serviceId: this.serviceId,
    } as ISolCustomerGetServiceParams;

    await this.getPickUpService(params);
    this.$emit("success");
  }

  async getPickUpService(params: ISolCustomerGetServiceParams): Promise<void> {
    const res = await this.fetchPickUpService(params);
    this.pickUpService = res?.pickUpService;
    this.service = res?.pickUpService;
  }

  async getAssistanceService(params: ISolCustomerGetServiceParams): Promise<void> {
    const res = await this.fetchAssistanceService(params);
    this.assistanceService = res?.assistanceService;
    this.service = res?.assistanceService;
  }

  async getDeliveryService(params: ISolCustomerGetServiceParams): Promise<void> {
    const res = await this.fetchDeliveryService(params);
    this.deliveryService = res?.deliverService;
    this.service = res?.deliverService;
  }

  async getSupplyMaterialServiceService(params: ISolCustomerGetServiceParams): Promise<void> {
    const res = await this.fetchSupplyMaterialService(params);
    this.supplyMaterialService = res?.supplyMaterialService;
    this.service = res?.supplyMaterialService;
  }

  get pickupConfig(): IOrderConfigCommon {
    return this.countryConfig?.Orders.Pickup ?? {};
  }

  get deliveryConfig(): IOrderConfigCommon {
    return this.countryConfig?.Orders.Delivery ?? {};
  }
}
